import type { GettextInterface } from "../../../../../../shared/I18n";
import type { ReportContent } from "../../../../viewjobOneGraphModel";

import { ContentProblem } from "./ContentProblem";
import { SubmitStatus } from "./SubmitStatus";
import eventEmitter from "../../../../../../shared/util/eventEmitter";
import { httpPostJson } from "../../../../../native-module/httpPostJson";

import { useCallback, useEffect, useMemo, useState } from "react";

const SUBMIT_REPORT_EVENT = "submitReport";

export function useModalState(): [boolean, () => void, () => void] {
  const [isOpen, setShowModal] = useState(false);
  const open = useCallback(() => setShowModal(true), [setShowModal]);
  const close = useCallback(() => setShowModal(false), [setShowModal]);
  return [isOpen, open, close];
}

interface ReportContentResponse {
  readonly message: string;
}

export function useSubmitReport(reportContent?: ReportContent): SubmitStatus {
  const [status, updateSubmitStatus] = useState(SubmitStatus.INACTIVE);

  useEffect(() => {
    const submitReport = ({ reason, moreInfo }: { reason: string; moreInfo: string }) => {
      if (!reportContent) {
        updateSubmitStatus(SubmitStatus.ERROR);
        return;
      }

      const { apiUrl, buildPayload } = reportContent;
      const payload = buildPayload(reason, moreInfo);

      httpPostJson<ReportContentResponse>(apiUrl, payload)
        .then((response) => {
          response.message === "OK"
            ? updateSubmitStatus(SubmitStatus.SUCCESS)
            : updateSubmitStatus(SubmitStatus.ERROR);
        })
        .catch(() => {
          updateSubmitStatus(SubmitStatus.ERROR);
        });
    };

    eventEmitter.on(SUBMIT_REPORT_EVENT, submitReport);

    return () => {
      // on unmount, remove the event listener
      eventEmitter.off(SUBMIT_REPORT_EVENT, submitReport);
    };
  }, [reportContent]);

  return status;
}

function labelForContentProblem(gt: GettextInterface, problem: ContentProblem): string {
  switch (problem) {
    case ContentProblem.EXPIRED:
      return gt.pgettext("report job expired text", "It is expired");
    case ContentProblem.FAKE:
      return gt.pgettext("report job fake text", "It seems like a fake job");
    case ContentProblem.INACCURATE:
      return gt.pgettext("report job inaccurate text", "It is inaccurate");
    case ContentProblem.OFFENSIVE:
      return gt.pgettext("report job offensive text", "It is offensive, discriminatory");
    case ContentProblem.ADVERTISEMENT:
      return gt.pgettext("report job advertisement text", "It is an advertisement");
    case ContentProblem.TECHNICAL_ISSUE:
      return gt.pgettext(
        "report job technical issue text",
        "It shows errors when viewing or applying",
      );
    case ContentProblem.PHOTO:
      return gt.pgettext("report job photo", "Images contain inappropriate content");
    case ContentProblem.OTHER:
      return gt.pgettext("report job other text", "Other");
  }
}

function useContentProblemOptions(gt: GettextInterface, showPhotoLabel: boolean) {
  return useMemo(() => {
    const contentProblems = [
      ContentProblem.OFFENSIVE,
      ContentProblem.FAKE,
      ContentProblem.INACCURATE,
      ContentProblem.ADVERTISEMENT,
    ];
    if (showPhotoLabel) {
      contentProblems.push(ContentProblem.PHOTO);
    }
    contentProblems.push(ContentProblem.OTHER);

    return contentProblems.map((problem) => ({
      value: problem.toString(),
      label: labelForContentProblem(gt, problem),
    }));
  }, [gt, showPhotoLabel]);
}

export function useReportContentForm(
  handleClose: () => void,
  showPhotoLabel: boolean,
  gt: GettextInterface,
  moreInfoMaxLength = 300,
) {
  const [reason, setReason] = useState("");
  const [moreInfo, setMoreInfo] = useState("");

  const reset = useCallback(() => {
    setReason("");
    setMoreInfo("");
  }, [setMoreInfo, setReason]);

  const close = useCallback(() => {
    reset();
    handleClose();
  }, [reset, handleClose]);

  const submit = useCallback(() => {
    close();
    return eventEmitter.emit(SUBMIT_REPORT_EVENT, { reason, moreInfo });
  }, [close, moreInfo, reason]);

  const canSubmit =
    (moreInfo.trim().length > 0 || reason !== "") && moreInfo.length <= moreInfoMaxLength;

  const contentProblemOptions = useContentProblemOptions(gt, showPhotoLabel);
  const selectedOption = useMemo(
    () => contentProblemOptions.find((item) => item.value === reason) ?? null,
    [contentProblemOptions, reason],
  );

  const selectProblemOption = useCallback(
    (item: { value: string }) => {
      setReason(item.value);
    },
    [setReason],
  );

  return {
    contentProblemOptions,
    selectProblemOption,
    selectedOption,
    moreInfo,
    setMoreInfo,
    close,
    submit,
    canSubmit,
  };
}
